import { DAY_ROUTE_COLORS } from '../../../hooks/useAllDayRoutes';
import { fitRouteShape, type Box, type FittedRouteShape } from './routeShape';
import {
  INK, PAPER, SAND, SERIF, MONO, STICKER_WIDTH, STICKER_HEIGHT,
  type StickerSpec, type StickerBackground, type StickerStat,
} from './stickerSpec';
import { drawTracked, fillVertical, truncate, wrapLines } from './canvasPaint';
import { coverRect } from './framing';
import { DEST_SAND, MARK_DEST, MARK_DOTS } from '../../brand/GromlyMark';

/**
 * The story sticker, painted at full 1080x1920 whatever size the preview is
 * shown at — the preview is the same canvas scaled down by CSS, so the exported
 * PNG can't drift from what the user arranged.
 */

const FULL: Box = { x: 0, y: 0, w: STICKER_WIDTH, h: STICKER_HEIGHT };
const MARGIN = 96;
const CONTENT_W = STICKER_WIDTH - MARGIN * 2;

const RULE_Y = 1500;
const STATS_BASELINE = 1600;
const ROUTE_BOX: Box = { x: MARGIN, y: 340, w: CONTENT_W, h: 880 };

interface Palette {
  text: string;
  faint: string;
  rule: string;
}

const ON_DARK: Palette = {
  text: PAPER,
  faint: 'rgba(250,247,240,0.62)',
  rule: 'rgba(217,179,130,0.45)',
};

const ON_PAPER: Palette = {
  text: INK,
  faint: 'rgba(11,20,32,0.52)',
  rule: 'rgba(11,20,32,0.16)',
};

export function paintSticker(canvas: HTMLCanvasElement, spec: StickerSpec): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = STICKER_WIDTH;
  canvas.height = STICKER_HEIGHT;
  ctx.textBaseline = 'alphabetic';

  const onPhoto = Boolean(spec.background.image);
  const light = spec.background.tone === 'light' && !onPhoto;
  const palette = light ? ON_PAPER : ON_DARK;

  paintBackground(ctx, spec.background);

  // The photo needs its ends darkened for the type to hold; cream is already
  // its own contrast.
  if (!light) {
    fillVertical(ctx, { x: 0, y: 0, w: STICKER_WIDTH, h: 560 }, [
      [0, 'rgba(11,20,32,0.58)'], [1, 'rgba(11,20,32,0)'],
    ]);
    fillVertical(ctx, { x: 0, y: 1080, w: STICKER_WIDTH, h: STICKER_HEIGHT - 1080 }, [
      [0, 'rgba(11,20,32,0)'], [0.45, 'rgba(11,20,32,0.72)'], [1, 'rgba(11,20,32,0.94)'],
    ]);
  }

  // On a photo the trip is one bright line, the way a GPS track reads over a
  // photo; on paper it can afford the per-day colours.
  if (spec.routeShape) paintRoute(ctx, spec.routeShape, onPhoto);

  paintFurniture(ctx, spec, palette);
  paintMark(ctx, spec.watermark, light);
}

function paintBackground(ctx: CanvasRenderingContext2D, background: StickerBackground): void {
  fillVertical(ctx, FULL, [[0, background.gradient[0]], [1, background.gradient[1]]]);
  if (!background.image) return;
  const r = coverRect(background.imageWidth, background.imageHeight, background.framing);
  ctx.drawImage(background.image, r.x, r.y, r.w, r.h);
}

/* ── Furniture ───────────────────────────────────────────────────────────── */

function paintFurniture(ctx: CanvasRenderingContext2D, spec: StickerSpec, palette: Palette): void {
  ctx.fillStyle = palette.text;
  ctx.font = `400 15px ${MONO}`;
  drawTracked(ctx, spec.subtitle.toUpperCase(), MARGIN, 168, 4.5);

  ctx.fillStyle = SAND;
  ctx.fillRect(MARGIN, 196, 96, 2);

  ctx.fillStyle = palette.rule;
  ctx.fillRect(MARGIN, RULE_Y, CONTENT_W, 2);

  paintStatRow(ctx, spec.stats, MARGIN, STATS_BASELINE, CONTENT_W, palette);

  ctx.font = `italic 300 92px ${SERIF}`;
  ctx.fillStyle = palette.text;
  const lines = wrapLines(ctx, spec.title, CONTENT_W, 3);
  let y = RULE_Y - 80;
  for (const line of [...lines].reverse()) {
    ctx.fillText(line, MARGIN, y);
    y -= 104;
  }
}

function paintStatRow(
  ctx: CanvasRenderingContext2D,
  stats: StickerStat[],
  x: number,
  baseline: number,
  width: number,
  palette: Palette,
): void {
  if (stats.length === 0) return;
  const column = width / stats.length;

  stats.forEach((stat, i) => {
    const left = x + column * i;
    ctx.font = `600 44px ${SERIF}`;
    ctx.fillStyle = palette.text;
    ctx.fillText(truncate(ctx, stat.value, column - 16), left, baseline);

    ctx.font = `400 15px ${MONO}`;
    ctx.fillStyle = palette.faint;
    drawTracked(ctx, stat.label.toUpperCase(), left, baseline + 34, 3.5);
  });
}

/* ── The route ───────────────────────────────────────────────────────────── */

function fitToBox(shape: NonNullable<StickerSpec['routeShape']>): FittedRouteShape | null {
  return fitRouteShape(shape, {
    x: ROUTE_BOX.x + 24, y: ROUTE_BOX.y + 24, w: ROUTE_BOX.w - 48, h: ROUTE_BOX.h - 48,
  });
}

function strokePaths(ctx: CanvasRenderingContext2D, fitted: FittedRouteShape, paint: (dayIdx: number) => void): void {
  for (const path of fitted.paths) {
    ctx.beginPath();
    path.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    paint(path.dayIdx);
  }
}

function paintRoute(
  ctx: CanvasRenderingContext2D,
  shape: NonNullable<StickerSpec['routeShape']>,
  onPhoto: boolean,
): void {
  const fitted = fitToBox(shape);
  if (!fitted) return;

  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  // A dashed line is how the sticker admits it's drawing a guess: no road
  // geometry came back, so the curve is interpolated between the stops.
  ctx.setLineDash(shape.approximate ? [18, 16] : []);

  if (onPhoto) {
    // The halo is what makes a white line survive a bright sky or a snowfield.
    ctx.shadowColor = 'rgba(11,20,32,0.55)';
    ctx.shadowBlur = 26;
    strokePaths(ctx, fitted, () => {
      ctx.strokeStyle = 'rgba(11,20,32,0.35)';
      ctx.lineWidth = 20;
      ctx.stroke();
    });
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    strokePaths(ctx, fitted, () => {
      ctx.strokeStyle = PAPER;
      ctx.lineWidth = 9;
      ctx.stroke();
    });
  } else {
    strokePaths(ctx, fitted, () => {
      ctx.strokeStyle = 'rgba(11,20,32,0.14)';
      ctx.lineWidth = 15;
      ctx.stroke();
    });
    strokePaths(ctx, fitted, dayIdx => {
      ctx.strokeStyle = DAY_ROUTE_COLORS[dayIdx % DAY_ROUTE_COLORS.length];
      ctx.lineWidth = 9;
      ctx.stroke();
    });
  }

  ctx.setLineDash([]);
  paintStopDots(ctx, fitted, onPhoto);
  ctx.restore();
}

/** Every stop, no ranking: a bead on the line rather than a numbered pin. */
function paintStopDots(ctx: CanvasRenderingContext2D, fitted: FittedRouteShape, onPhoto: boolean): void {
  for (const stop of fitted.stops) {
    ctx.beginPath();
    ctx.arc(stop.x, stop.y, 7, 0, Math.PI * 2);
    ctx.fillStyle = onPhoto ? PAPER : INK;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(stop.x, stop.y, 3, 0, Math.PI * 2);
    ctx.fillStyle = onPhoto ? INK : PAPER;
    ctx.fill();
  }
}


/* ── Mark ────────────────────────────────────────────────────────────────── */

/** The Gromly mark, drawn rather than loaded: an <img> of the SVG would have
 *  to decode before every paint, and this way its dots take the sticker's ink. */
function drawMonogram(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, light: boolean): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 100, size / 100);
  ctx.translate(-2, 1);
  ctx.fillStyle = light ? INK : PAPER;
  for (const d of MARK_DOTS) {
    ctx.beginPath();
    ctx.arc(d.cx, d.cy, d.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = DEST_SAND;
  ctx.beginPath();
  ctx.arc(MARK_DEST.cx, MARK_DEST.cy, MARK_DEST.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A 108px mark with the wordmark at 0.8 of it, on the mark's own baseline. */
const MARK_SIZE = 108;
const MARK_GAP = 20;

function paintMark(ctx: CanvasRenderingContext2D, wordmark: string, light: boolean): void {
  const fontSize = Math.round(MARK_SIZE * 0.8);
  ctx.font = `italic 300 ${fontSize}px ${SERIF}`;
  const total = MARK_SIZE + MARK_GAP + ctx.measureText(wordmark).width;
  const x = (STICKER_WIDTH - total) / 2;
  const top = STICKER_HEIGHT - 240;

  drawMonogram(ctx, x, top, MARK_SIZE, light);

  ctx.fillStyle = light ? INK : PAPER;
  ctx.fillText(wordmark, x + MARK_SIZE + MARK_GAP, top + MARK_SIZE * 0.75);
}
